import React, { useState, useEffect, useRef } from "react";
import jsPDF from "jspdf";
import { Canvas, Textbox, Image } from "fabric";
import Settings from "./Settings";
import krzyz1 from "../../../img/krzyz1.png";
import demo from "../../../img/demo.png";
import { Link as RouterLink } from "react-router-dom";
import SettingsIcon from "@mui/icons-material/Settings";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import "./Demo.scss";
import { Helmet } from "react-helmet-async";
import axios from "../../../axios";

const MIN_DOWNLOAD_SPINNER_MS = 650;
const waitForNextPaint = () =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
const waitForMinimumSpinner = (startedAt) =>
  new Promise((resolve) =>
    setTimeout(resolve, Math.max(0, MIN_DOWNLOAD_SPINNER_MS - (Date.now() - startedAt))),
  );

const Demo = () => {
  const [, setIsLoading] = useState(true);
  const [buttonDisabledPDF, setButtonDisabledPDF] = useState(false);
  const [buttonDisabledJPG, setButtonDisabledJPG] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const isDownloading = buttonDisabledPDF || buttonDisabledJPG;

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [canvas, setCanvas] = useState(null);

  const originalWidth = 1000;
  const originalHeight = 707;

  const resizeCanvas = (fabricCanvas) => {
    if (!fabricCanvas || !fabricCanvas.lowerCanvasEl || !containerRef.current)
      return;

    const containerWidth = containerRef.current.offsetWidth;
    const scale = containerWidth / originalWidth;

    const newWidth = originalWidth * scale;
    const newHeight = originalHeight * scale;

    // Zoom
    fabricCanvas.renderAll();

    // lower-canvas
    fabricCanvas.lowerCanvasEl.style.width = `${newWidth}px`;
    fabricCanvas.lowerCanvasEl.style.height = `${newHeight}px`;

    // containerRef
    containerRef.current.style.height = `${newHeight}px`;

    // canvas-container
    const canvasContainer =
      containerRef.current.querySelector(".canvas-container");
    if (canvasContainer) {
      canvasContainer.style.width = `${newWidth}px`;
      canvasContainer.style.height = `${newHeight}px`;
    }

    // upper-canvas, kluczowy element
    const upperCanvas = containerRef.current.querySelector(".upper-canvas");
    if (upperCanvas) {
      upperCanvas.style.width = `${newWidth}px`;
      upperCanvas.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    setIsLoading(false);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 800);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!successMessage && !errorMessage) return undefined;

    const timeoutId = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 4500);

    return () => clearTimeout(timeoutId);
  }, [successMessage, errorMessage]);

  useEffect(() => {
    if (!canvasRef.current) return; // dodaj to

    // Zniszcz wcześniej istniejący canvas jeśli jest
    if (canvasRef.current.fabric) {
      canvasRef.current.fabric.dispose();
    }

    const initCanvas = new Canvas(canvasRef.current, {
      width: originalWidth,
      height: originalHeight,
      useCORS: true,
      allowTaint: false,
      preserveObjectStacking: true,
    });

    // Dodaj teksty

    const textElements = [
      {
        text: "Bóg sam wybrał czas, by powołać Cię do siebie...",
        top: 100,
        left: (originalWidth - 580) / 2,
        fontSize: 25,
        width: 650,
        fontWeight: "normal",
        fontFamily: "Bitter",
        fontStyle: "italic",
      },
      {
        text: "Z głębokim żalem zawiadamiamy, że dnia 15.04.2024 r. odeszła od nas w wieku 84 lat nasza kochana Mama, Babcia i Prababcia",
        top: 150,
        left: (originalWidth - 580) / 2,
        fontSize: 23,
        width: originalWidth * 0.65,
        fontWeight: "normal",
        fontFamily: "Bitter",
      },
      {
        text: "śp. Hanna Kowalska",
        top: 280,
        left: (originalWidth * 0.1) / 2,
        fontSize: 90,
        width: originalWidth * 0.9,
        fontWeight: "bold",
        fontFamily: "DM Serif Display",
      },
      {
        text: "Msza św. żałobna zostanie odprawiona w czwartek 17.04.2024 r. o godzinie 12:00 w kościele pw. Św. Antoniego w Warszawie, po której nastąpi złożenie Ciała do grobu na cmentarzu parafialnym o godzinie 13:00.",
        top: 425,
        left: (originalWidth - originalWidth * 0.7) / 2,
        fontSize: 22,
        width: originalWidth * 0.7,
        fontWeight: "normal",
        fontFamily: "Bitter",
      },
      {
        text: "O czym zawiadamia pogrążona w smutku rodzina",
        top: 550,
        left: (originalWidth - 200) / 2,
        fontSize: 18,
        width: originalWidth * 0.5,
        fontWeight: "bold",
        fontFamily: "Bitter",
      },
    ];

    textElements.forEach(
      ({ text, top, left, fontSize, width, fontWeight, fontFamily }) => {
        const textbox = new Textbox(text, {
          top,
          left,
          fontSize,
          textAlign: "center",
          width,
          fontWeight,
          fontFamily,
        });
        initCanvas.add(textbox);
      },
    );

    // Tło
    const imageElement = document.createElement("img");
    imageElement.crossOrigin = "anonymous";
    imageElement.src = demo;
    imageElement.onload = () => {
      const backgroundImage = new Image(imageElement);
      backgroundImage.scaleToWidth(originalWidth);
      backgroundImage.scaleToHeight(originalHeight);
      const bgWidth = backgroundImage.getScaledWidth();
      const bgHeight = backgroundImage.getScaledHeight();
      initCanvas.backgroundImage = backgroundImage;
      initCanvas.backgroundImage.set({
        left: (originalWidth - bgWidth) / 2,
        top: (originalHeight - bgHeight) / 2,
      });

      // Krzyż
      const crossImage = document.createElement("img");
      crossImage.crossOrigin = "anonymous";
      crossImage.src = krzyz1;
      crossImage.onload = () => {
        const image = new Image(crossImage);
        image.scaleToWidth(95);
        image.set({ left: 110, top: 95, selectable: true });
        initCanvas.add(image);
      };

      initCanvas.renderAll();
    };

    setCanvas(initCanvas);
    resizeCanvas(initCanvas);

    const handleResize = () => resizeCanvas(initCanvas);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      initCanvas.dispose();
    };
  }, []);

  const handleDownloadPDF = async () => {
    if (!canvas) return;
    const startedAt = Date.now();
    let nextSuccessMessage = "";
    let nextErrorMessage = "";

    setButtonDisabledPDF(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      await waitForNextPaint();
      await logger();
      canvas.discardActiveObject();
      canvas.renderAll();

      const imgData = canvas.toDataURL({
        format: "png",
        multiplier: 4,
        enableRetinaScaling: true,
      });

      const pdf = new jsPDF("landscape", "pt", "a4");
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = pdf.internal.pageSize.getHeight();

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save("nekrolog.pdf");
      nextSuccessMessage = "PDF został pobrany.";
    } catch (error) {
      console.error("Błąd przy generowaniu PDF:", error);
      nextErrorMessage = "Nie udało się pobrać PDF. Spróbuj ponownie.";
    } finally {
      await waitForMinimumSpinner(startedAt);
      setButtonDisabledPDF(false);
      setSuccessMessage(nextSuccessMessage);
      setErrorMessage(nextErrorMessage);
    }
  };

  const handleDownloadJPG = async () => {
    if (!canvas) return;
    const startedAt = Date.now();
    let nextSuccessMessage = "";
    let nextErrorMessage = "";

    setButtonDisabledJPG(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      await waitForNextPaint();
      await logger();
      canvas.discardActiveObject();
      canvas.renderAll();

      const imgData = canvas.toDataURL({
        format: "jpeg",
        quality: 1.0,
        multiplier: 4,
      });

      const link = document.createElement("a");
      link.href = imgData;
      link.download = "nekrolog.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      nextSuccessMessage = "JPG został pobrany.";
    } catch (error) {
      console.error("Błąd przy generowaniu JPG:", error);
      nextErrorMessage = "Nie udało się pobrać JPG. Spróbuj ponownie.";
    } finally {
      await waitForMinimumSpinner(startedAt);
      setButtonDisabledJPG(false);
      setSuccessMessage(nextSuccessMessage);
      setErrorMessage(nextErrorMessage);
    }
  };

  const logger = async () => {
    try {
      await axios.get("/demo");
    } catch (error) {
      console.log("nie udało się");
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Demo - Nekrodruk.pl | Wypróbuj generator nekrologów bez logowania
        </title>
        <meta
          name="description"
          content="Wypróbuj za darmo nowoczesny generator nekrologów online - bez logowania i rejestracji. Nekrodruk.pl to szybkie tworzenie klepsydr, szarf pogrzebowych i tabliczek dla zakładów pogrzebowych i osób prywatnych."
        />
        <meta
          name="keywords"
          content="demo nekrologu, generator nekrologów, nekrolog bez logowania, szarfa pogrzebowa, test nekrologu, nekrodruk demo, klepsydra online, generator tabliczek, generator klepsydry, klepsydra pogrzebowa generator, kreator nekrologów"
        />
        <meta name="author" content="Nekrodruk.pl" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      <section className="Demo_section">
        <div className="demoCard">
          <div className="demoHeader">
            <h1>Demo - generator nekrologów</h1>
            <p>
              Wypróbuj bez logowania. Zaznacz element na podglądzie, by go
              edytować.
            </p>
          </div>
          <div
            className="canvas-wrapper"
            ref={containerRef}
            style={{ width: "100%", maxWidth: "1000px", margin: "0 auto" }}
          >
            <canvas ref={canvasRef} />
          </div>
          <div className="btnsWrapper">
            <button
              className="btnDownload"
              onClick={handleDownloadPDF}
              disabled={isDownloading}
            >
              Pobierz jako PDF
            </button>
            <button
              className="btnDownload"
              onClick={handleDownloadJPG}
              disabled={isDownloading}
            >
              Pobierz jako JPG
            </button>
          </div>
          {isDownloading && (
            <div className="downloadLoadingStatus">
              <CircularProgress size={24} />
              <span>
                {buttonDisabledPDF ? "Generowanie PDF..." : "Generowanie JPG..."}
              </span>
            </div>
          )}
          {successMessage && (
            <Alert
              severity="success"
              onClose={() => setSuccessMessage("")}
              className="demoSuccessAlert"
            >
              {successMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert
              severity="error"
              onClose={() => setErrorMessage("")}
              className="demoSuccessAlert"
            >
              {errorMessage}
            </Alert>
          )}
          <div className="demoCta">
            <p className="demoCtaTitle">Chcesz więcej możliwości?</p>
            <p className="demoCtaText">
              Zarejestruj się i twórz nekrologi, szarfy i tabliczki z pełnym
              zestawem szablonów, personalizacją i eksportem do PDF.
            </p>
            <RouterLink to="/register" className="demoCtaBtn">
              Dołącz do nas
            </RouterLink>
          </div>
        </div>
        {canvas && (
          <>
            {isMobile && isSettingsOpen && (
              <div
                className="settingsOverlay"
                onClick={() => setIsSettingsOpen(false)}
              />
            )}
            <Settings
              canvas={canvas}
              isMobile={isMobile}
              isOpen={!isMobile || isSettingsOpen}
              onClose={() => setIsSettingsOpen(false)}
            />
            {isMobile && !isSettingsOpen && (
              <button
                className="settingsTab"
                onClick={() => setIsSettingsOpen(true)}
                aria-label="Otwórz ustawienia"
              >
                <SettingsIcon />
              </button>
            )}
          </>
        )}
      </section>
    </>
  );
};

export default Demo;
